import { useEffect, useState } from 'react'
import { Box, Spinner, Text, VStack } from '@chakra-ui/react'
import { useLocation } from 'react-router-dom'
import { useGrantCampaign } from '../../hooks/useGrantCampaign'
import { useGrantSession } from '../../hooks/useGrantSession'
import { useMyTurfs } from '../../hooks/useCanvasser'
import { useToday } from '../../hooks/useToday'
import { isCacheExpiredError } from '../../lib/offlineStore'
import { openableCards, type TodayCard } from '../../lib/todayAssignments'
import { readChosenAssignment } from '../../lib/walkPrefs'
import { CacheExpiredNotice } from '../../components/canvasser/CacheExpiredNotice'
import { WaitingRoom } from '../../components/canvasser/WaitingRoom'
import { WalkScreen } from './WalkScreen'
import { ActionRouteScreen } from './ActionRouteScreen'
import { CallBatchScreen } from './CallBatchScreen'
import { TodayPage } from './TodayPage'

/** How often the waiting room re-checks for a turf assignment. */
export const HOME_POLL_MS = 30_000

/** The longest the home shows a bare spinner before falling back to the waiting room (still
 *  checking) from the campaign mirror — a hanging connection otherwise spins for ~2 minutes. */
export const HOME_SPINNER_MAX_MS = 3_000

export type HomeTarget =
  | { kind: 'waiting' } | { kind: 'today' } | { kind: 'open'; card: TodayCard; showTodayLink: boolean }

/**
 * [CF-1 P3] What the home shows: no assignment → waiting room; one → open it (no Today link);
 * 2+ → the remembered choice if it is still one of them (with a Today link), else the Today picker.
 * A CallBatch card (CF-1 P4) is admitted exactly like a turf card.
 */
export function homeTarget(cards: TodayCard[], chosenKey: string | null): HomeTarget {
  const openable = openableCards(cards)
  if (openable.length === 0) return { kind: 'waiting' }
  if (openable.length === 1) return { kind: 'open', card: openable[0], showTodayLink: false }
  const chosen = chosenKey ? openable.find(c => c.key === chosenKey) : undefined
  return chosen ? { kind: 'open', card: chosen, showTodayLink: true } : { kind: 'today' }
}

/**
 * [Canvasser redesign Phase 1 / spec D1, D2, §4.1] `/canvasser` — the canvasser always lands IN
 * their one campaign. With assignments it opens one (`homeTarget`: the walk, an action route, or
 * the Today picker for 2+); without, the waiting room, which polls
 * and refetches on focus so an assignment appears without a reload. There is no picker: a grant
 * with no campaign gets a plain notice.
 */
export function CampaignHomePage() {
  const campaign = useGrantCampaign()
  const { identity } = useGrantSession()
  // The waiting-room poll; useToday reads the same turfs query for the decision.
  const turfs = useMyTurfs(campaign?.id, { pollWhileEmptyMs: HOME_POLL_MS })
  const today = useToday(campaign?.id)
  // Subscribes to navigation: a Today tap (or a door's navigate('/canvasser')) lands on this same
  // route, so the remembered choice is re-read on every render rather than memoized.
  useLocation()
  const [spinnerExpired, setSpinnerExpired] = useState(false)
  const isLoading = turfs.isLoading || today.isLoading

  useEffect(() => {
    if (!isLoading) return
    const t = setTimeout(() => setSpinnerExpired(true), HOME_SPINNER_MAX_MS)
    return () => clearTimeout(t)
  }, [isLoading])

  if (!campaign) {
    return (
      <VStack py={16} px={6} gap={2}>
        <Text fontWeight="semibold" fontSize="lg" textAlign="center">
          Your access isn't linked to a campaign
        </Text>
        <Text color="fg.muted" fontSize="sm" textAlign="center">
          Ask your organizer for a new link or code.
        </Text>
      </VStack>
    )
  }

  if (isLoading && !spinnerExpired) {
    return <VStack py={16}><Spinner size="lg" color="accent" /></VStack>
  }

  if (turfs.error && isCacheExpiredError(turfs.error)) return <CacheExpiredNotice />

  const target = homeTarget(today.cards, readChosenAssignment(campaign.id))
  if (target.kind === 'today') return <TodayPage />
  if (target.kind === 'open') {
    const { card, showTodayLink } = target
    // [CF-1 P4] openableCards only admits a CallBatch card that carries a callBatchId.
    if (card.kind === 'CallBatch') {
      return <CallBatchScreen key={card.callBatchId!} callBatchId={card.callBatchId!} showTodayLink={showTodayLink} />
    }
    const turfId = card.turfId!
    // Canvass and ChaseRoute open the normal walk + door flow (spec §7.4).
    return card.kind === 'ActionRoute'
      // key={turfId}: switching assignments remounts, so no per-turf state (e.g. lastRecorded) carries over.
      ? <ActionRouteScreen key={turfId} turfId={turfId} showTodayLink={showTodayLink} />
      : <WalkScreen key={turfId} turfId={turfId} showTodayLink={showTodayLink} />
  }

  return (
    <Box>
      <WaitingRoom
        campaign={campaign}
        firstName={identity?.subjectFirstName ?? null}
        isChecking={turfs.isFetching || turfs.isLoading}
        isOffline={!!turfs.error}
        showPhotoPrompt={identity != null && !identity.portraitUrl}
        onCheckAgain={() => void turfs.refetch()}
      />
    </Box>
  )
}
